// *** AXIOS SERVICE ***
// Used for backend requests.

import Vue from 'vue';
import { http } from '@/http';
import { i18n } from '@/main';

// *** HANDLE AXIOS ERROR ***
// Called in the catch block of every backend API function call.
export const handleAxiosError = error => {
    const errorMessage =
        error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message;
    if ((!error.response || error.response.status !== 401) && errorMessage) {
        // show toast error if non-401 error response received from backend with message
        Vue.prototype.$toastr.e(i18n.te(errorMessage) ? i18n.t(errorMessage) : errorMessage);
    }
};

// *** CREATE CUSTOM MEDICATION ***
export const createCustomMedication = async data => {
    const response = await http.post('custom-medication', data);
    return response.data.result;
};

// *** CREATE DRUGSAFE USER ***
export const createDrugSafeUser = async data => {
    const response = await http.post('safe-user', data);
    return response.data.result;
};

// *** CREATE LOGIN-LOGOUT LOG ***
export const createLoginLogoutLog = async data => {
    const response = await http.post('login-logout-log', data);
    return response.data.result;
};

// *** CREATE PHARMACY ***
export const createPharmacy = async data => {
    const response = await http.post('pharmacy', data);
    return response.data.result;
};

// *** CREATE ROLE ***
export const createRole = async data => {
    const response = await http.post('role', data);
    return response.data.result;
};

// *** CREATE RESERVATION ***
export const createReservation = async data => {
    const response = await http.post('reservation', data);
    return response.data.result;
};

// *** CREATE WEB USER ***
export const createWebUser = async data => {
    const response = await http.post('web-user', data);
    return response.data.result;
};

// *** CREATE CUSTOM MEDICATION ***
export const deleteCustomMedication = async vnr => {
    const response = await http.delete(`custom-medication/${vnr}`);
    return response.data.result;
};

// *** DELETE DRUGSAFE USER ***
export const deleteDrugSafeUser = async (pharmacyId, userId) => {
    const response = await http.delete(`safe-user/${pharmacyId}/${userId}`);
    return response.data.result;
};

// *** DELETE PHARMACY ***
export const deletePharmacy = async pharmacyId => {
    const response = await http.delete(`pharmacy/${pharmacyId}`);
    return response.data.result;
};

// *** DELETE RESERVATION ***
export const deleteReservation = async res_id => {
    const response = await http.delete(`reservation/${res_id}`);
    return response.data.result;
};

// *** DELETE WEB USER ***
export const deleteWebUser = async userId => {
    const response = await http.delete(`web-user/${userId}`);
    return response.data.result;
};

// *** GET ADDRESS ***
export const getAddress = async () => {
    const response = await http.get('address');
    return response.data.result;
};

// *** GET CUSTOM MEDICATION ***
export const getCustomMedication = async vnr => {
    const response = await http.get(`custom-medication/${vnr}`);
    return response.data.result;
};

// *** GET CUSTOM MEDICATIONS ***
export const getCustomMedications = async () => {
    const response = await http.get('custom-medications');
    return response.data.result;
};

// *** GET DRUG CARD ***
export const getDrugCard = async (pharmacyId, params) => {
    const response = await http.get(`drug-card/${pharmacyId}`, params);
    return response.data.result;
};

// *** GET DRUGSAFE USER ***
export const getDrugSafeUser = async (pharmacyId, userId) => {
    const response = await http.get(`safe-user/${pharmacyId}/${userId}`);
    return response.data.result;
};

// *** GET DRUGSAFE LANGUAGES ***
export const getDrugSafeLanguages = async () => {
    const response = await http.get('drugsafe-languages');
    return response.data.result;
};

// *** GET DRUGSAFE USERS ***
export const getDrugSafeUsers = async pharmacyId => {
    const response = await http.get(`safe-users/${pharmacyId}`);
    return response.data.result;
};

// *** GET MEDICATIONS BASED ON SEARCH PARAMEERS ***
export const getFilteredMedications = async params => {
    const response = await http.get('filtered-medications', params);
    return response.data.result;
};

// *** GET PHARMACY'S FREE WEB USERS ***
export const getFreeWebUsersForPharmacy = async pharmacyId => {
    const response = await http.get(`web-users-pharmacy-free/${pharmacyId}`);
    return response.data.result;
};

// *** GET LOG ***
export const getLog = async (pharmacyId, logId) => {
    const response = await http.get(`log/${pharmacyId}/${logId}`);
    return response.data.result;
};

// *** GET LOGS ***
export const getLogs = async (pharmacyId, params) => {
    const response = await http.get(`logs/${pharmacyId}`, params);
    return response.data.result;
};

// *** GET MEDICATION'S AVAILABLE AMOUNT ***
export const getMedicationAvailableAmount = async vnr => {
    const response = await http.get(`medication-available-amount/${vnr}`);
    return response.data.result;
};

// *** GET MEDICATION'S IMFORMATION FROM INVENTORY (in case if medication is removed from online DB)
export const getMedicationInventory = async vnr => {
    const response = await http.get(`search-medication-inventory/${vnr}`);
    return response.data.result;
};

// *** GET PHARMACIES ***
export const getPharmacies = async getWebUsers => {
    const response = await http.get(`pharmacies/${getWebUsers}`);
    return response.data.result;
};

// *** GET PHARMACY ***
export const getPharmacy = async pharmacyId => {
    const response = await http.get(`pharmacy/${pharmacyId}`);
    return response.data.result;
};

// *** GET PHARMACY'S ALARM LOGS ***
export const getPharmacyAlarms = async pharmacyId => {
    const response = await http.get(`pharmacy-alarms/${pharmacyId}`);
    return response.data.result;
};

// *** GET PHARMACY'S CONFIG ***
export const getPharmacyConfig = async pharmacyId => {
    const response = await http.get(`pharmacy-config/${pharmacyId}`);
    return response.data.result;
};

// *** GET PHARMACY'S LOCKERS ***
export const getPharmacyLockers = async () => {
    const response = await http.get('pharmacy-lockers');
    return response.data.result;
};

// *** GET WEB USER PROFILE ***
export const getProfile = async () => {
    const response = await http.get('profile');
    return response.data.result;
};

// *** GET RESERVATION WITH RES_ID ***
export const getReservation = async res_id => {
    const response = await http.get(`reservations/${res_id}`);
    return response.data.result;
};

// *** GET RESERVATIONS ***
export const getReservations = async () => {
    const response = await http.get('reservations');
    return response.data.result;
};

// *** GET ROLE ***
export const getRole = async roleId => {
    const response = await http.get(`role/${roleId}`);
    return response.data.result;
};

// *** GET ROLES ***
export const getRoles = async platform => {
    const response = await http.get(`roles/${platform ? platform : ''}`);
    return response.data.result;
};

// *** GET USED MEDICATIONS ***
export const getUsedMedications = async () => {
    const response = await http.get('used-medications');
    return response.data.result;
};

// *** GET WEB USER ***
export const getWebUser = async userId => {
    const response = await http.get(`web-user/${userId}`);
    return response.data.result;
};

// *** GET ALL WEB USERS (ANY PHARMACY) ***
export const getWebUsers = async () => {
    const response = await http.get('web-users');
    return response.data.result;
};

// *** GET PHARMACY'S WEB USERS ***
export const getWebUsersForPharmacy = async pharmacyId => {
    const response = await http.get(`web-users-pharmacy/${pharmacyId}`);
    return response.data.result;
};

// *** RESET WEB USER PASSWORD ***
export const resetWebUserPassword = async data => {
    const response = await http.post('web-user-password-reset', data);
    return response.data.result;
};

// *** SEARCH (NOT IN USE) ***
export const search = async data => {
    const response = await http.post('search', data);
    return response.data.result;
};

// *** SET ADDRESS (NOT IN USE) ***
export const setAddress = async data => {
    const response = await http.post('address', data);
    return response.data.result;
};

// *** SET FAILED WEB USER LOGIN ***
export const setFailedLogin = async data => {
    const response = await http.put('web-user-failed-login', data);
    return response.data.result;
};

// *** SIGN LOG ***
export const signLog = async (pharmacyId, data) => {
    const response = await http.put(`log-sign/${pharmacyId}`, data);
    return response.data.result;
};

// *** UPDATE CUSTOM MEDICATION ***
export const updateCustomMedication = async data => {
    const response = await http.put('custom-medication', data);
    return response.data.result;
};

// *** UPDATE CUSTOM MEDICATION'S ACTIVE STATE ***
export const updateCustomMedicationActiveState = async vnr => {
    const response = await http.put(`custom-medication-active/${vnr}`);
    return response.data.result;
};

// *** UPDATE DEFAULT LOCKER ***
export const updateDefaultLocker = async data => {
    const response = await http.put('default-locker', data);
    return response.data.result;
};

// *** UPDATE DRUGSAFE USER ***
export const updateDrugSafeUser = async (pharmacyId, userId, data) => {
    const response = await http.put(`safe-user/${pharmacyId}/${userId}`, data);
    return response.data.result;
};

// *** UPDATE LOG ***
export const updateLog = async (pharmacyId, log) => {
    const response = await http.put(`log/${pharmacyId}`, log);
    return response.data.result;
};

// *** UPDATE MEDICATION'S LOW-INVENTORY ALERT CONFIG ***
export const updateLowInventoryAlert = async (pharmacyId, data) => {
    const response = await http.put(`low-inventory-alert/${pharmacyId}`, data);
    return response.data.result;
};

// *** UPDATE PHARMACY ***
export const updatePharmacy = async (pharmacyId, data) => {
    const response = await http.put(`pharmacy/${pharmacyId}`, data);
    return response.data.result;
};

// *** UPDATE DrugSafe Structure (add or remove modules) ***
export const updateDrugSafeStructure = async (pharmacyId, data) => {
    const response = await http.put(`pharmacy-drugsafe/${pharmacyId}`, data);
    return response.data.result;
};

// *** UPDATE RESERVATION ***
export const updateReservation = async data => {
    const response = await http.put('reservation', data);
    return response.data.result;
};

// *** UPDATE ROLE ***
export const updateRole = async (roleId, data) => {
    const response = await http.patch(`roles/${roleId}`, data);
    return response.data.result;
};

// *** UPDATE WEB USER ***
export const updateWebUser = async (userId, data) => {
    const response = await http.put(`web-user/${userId}`, data);
    return response.data.result;
};
